import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useStoreStore } from '@/stores/store-store';
import { useShipmentsStore } from '@/stores/shipments-store';
import { useInboundShipmentItemsStore } from '@/stores/inbound-shipment-items-store';
import { Loader2, ChevronsUpDown, PackageCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import SplitShipmentItemsTable from './SplitShipmentItemsTable';

type Props = {
  className?: string;
  onShipmentSubmitClick?: () => void;
};

function CustomCheckbox() {
  return (
    <div className="relative">
      <input 
        type="checkbox" 
        className="peer w-4 h-4 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 bg-white appearance-none checked:bg-blue-600 checked:border-blue-600" 
      />
      <svg 
        className="absolute inset-0 w-4 h-4 pointer-events-none opacity-0 peer-checked:opacity-100 text-white" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
}

export default function ShipmentItemsTable({ onShipmentSubmitClick }: Props) {
  const token = useAuthStore((s) => s.token);
  const currentStore = useStoreStore((s) => s.currentStore);
  // const { shipments, total, availableMarketplaces, isLoading, error, fetchShipments } = useShipmentsStore();
  // const { items: shipmentItems, isLoading: itemsLoading, error: itemsError, fetchItems } = useInboundShipmentItemsStore();
  // const [selectedShipmentId, setSelectedShipmentId] = useState<number | null>(null);
  const { shipments, total, isLoading, error, fetchShipments } = useShipmentsStore();
  const { fetchItems } = useInboundShipmentItemsStore();
  const [selectedShipmentId, setSelectedShipmentId] = useState<number | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showSplitTable, setShowSplitTable] = useState(false);



  useEffect(() => {
    if (!token || !currentStore?.id) return;
    fetchShipments({ token, storeId: currentStore.id, page, limit: itemsPerPage });
  }, [token, currentStore?.id, fetchShipments, page, itemsPerPage]);

  // Fetch shipment items when a shipment is selected
  useEffect(() => {
    if (!token || !currentStore?.id || !selectedShipmentId) return;
    fetchItems({ token, storeId: currentStore.id, shipmentId: selectedShipmentId });
  }, [token, currentStore?.id, selectedShipmentId, fetchItems]);

  // Listen for refresh events from operation page
  useEffect(() => {
    const handleRefreshOperationData = () => {
      console.log('🔄 Refreshing shipment items data...');
      if (!token || !currentStore?.id) return;
      
      // Refresh shipments list
      fetchShipments({ token, storeId: currentStore.id, page, limit: itemsPerPage });
      
      // Refresh shipment items if a shipment is selected
      if (selectedShipmentId) {
        fetchItems({ token, storeId: currentStore.id, shipmentId: selectedShipmentId });
      }
    };

    window.addEventListener('refresh-operation-data', handleRefreshOperationData);
    
    return () => {
      window.removeEventListener('refresh-operation-data', handleRefreshOperationData);
    };
  }, [token, currentStore?.id, selectedShipmentId, fetchShipments, fetchItems, page, itemsPerPage]);

  const headers = [
    'Actions',
    'References',
    'Order Date',
    'Marketplace',
    'Quantity',
    'Master Way Bill',
    'Weight',
    'Total',
  ];

  // Get pagination data from shipments store
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

  const handlePageChange = (newPage: number) => setPage(newPage);
  
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1); // Reset to first page when changing items per page
  };

  const handleReceiveClick = (shipmentId: number) => {
    setSelectedShipmentId(shipmentId);
    setShowSplitTable(true);
  };

  const handleBackToShipments = () => {
    setShowSplitTable(false);
    setSelectedShipmentId(null);
    
    // Refresh the shipments data when navigating back
    if (token && currentStore?.id) {
      console.log('🔄 Refreshing shipments data after split table...');
      fetchShipments({ token, storeId: currentStore.id, page, limit: itemsPerPage });
    }
    
    // Dispatch refresh event to update the main table data
    const refreshEvent = new CustomEvent('refresh-operation-data');
    window.dispatchEvent(refreshEvent);
  };



  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mx-16 p-8">
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mx-16 p-8">
        <div className="text-center text-sm text-red-600">{error}</div>
      </div>
    );
  }

    // Show split table view when a shipment is selected
  if (showSplitTable && selectedShipmentId) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mx-16">
        {/* Header with back button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackToShipments}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={16} />
              Back to Shipments
            </button>
            <h2 className="text-lg font-semibold text-gray-900">
              Receiving Shipment: {shipments.find(s => s.id === selectedShipmentId)?.shipment_reference}
            </h2>
          </div>
        </div>

        {/* Use SplitShipmentItemsTable component for shipment items (which are orders) */}
        <SplitShipmentItemsTable 
          shipmentId={selectedShipmentId}
          storeId={currentStore?.id || 0}
          onShipmentSubmitClick={onShipmentSubmitClick}
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mx-16">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#EDF1FF] border-b border-gray-200">
              <th className="py-3 px-4 text-left border-r border-gray-200">
                <CustomCheckbox />
              </th>
              {headers.map((h) => (
                <th key={h} className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">
                  <div className="flex items-center gap-1">
                    {h}
                    <ChevronsUpDown size={14} className="text-gray-400" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {shipments.map((shipment) => (
              <tr key={shipment.id} className="hover:bg-gray-50">
                <td className="py-3 px-4 border-r border-gray-200">
                  <CustomCheckbox />
                </td>
                <td className="py-3 px-4 border-r border-gray-200">
                  <button 
                    onClick={() => handleReceiveClick(shipment.shid)}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-purple-500 text-white text-sm font-medium hover:bg-purple-600 transition-colors"
                  >
                    <PackageCheck size={14} />
                    Receive
                  </button>
                </td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{shipment.shipment_reference}</td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{new Date(shipment.created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{shipment.mkt_name || 'N/A'}</td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{shipment.qty || 1}</td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{shipment.master_way_bill || 'N/A'}</td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                  {shipment.weight ? `${shipment.weight} kg` : 'N/A'}
                </td>
                <td className="py-3 px-4 text-sm text-gray-900">{shipment.total || 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer with pagination */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Show</span>
          <select 
            value={itemsPerPage} 
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span className="text-sm text-gray-700">per page</span>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-700">
            {`${(page - 1) * itemsPerPage + 1}-${Math.min(page * itemsPerPage, total)} of ${total}`}
          </span>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => handlePageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => handlePageChange(p)}
                className={`px-3 py-1 text-sm rounded ${page === p ? 'bg-[#F1F1F4] text-gray-900 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                {p}
              </button>
            ))}
            <button 
              onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {shipments.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No data available</p>
        </div>
      )}
    </div>
  );
}
